import { Tooltip } from 'antd';
import { Dictionary } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Category } from 'api/timesheets/categories';
import { TimesheetCategoryReport } from 'api/timesheets/reports/types';
import { Tag } from 'components/atoms/tag';
import { CategoryName } from 'components/organisms/timesheets/common/category-name';
import { PercentWithTimeCell } from 'components/organisms/timesheets/common/cells/percent-with-time-cell';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { calculatePercentage } from 'components/organisms/timesheets/common/utils';
import { getRandomColor } from 'utils/get-random-color';
import {
  billableMinutesSorter,
  categoryNameSorter,
  nonBillableMinutesSorter,
  totalMinutesSorter,
} from './sorter';

const HoverableBaseCell = styled(BaseCell)<{ clickable }>`
  ${ifProp(
    'clickable',
    css`
      cursor: pointer;
    `,
  )};
`;
const TagBaseCell = styled(HoverableBaseCell)`
  padding: var(--spacing-04) var(--spacing-05);

  ${Tag} {
    margin: 0;
    max-width: 100%;
  }
`;

export const useOverallCategoryConfig = ({
  noOfRows,
  onNameCellClick,
  timesheetCategories,
  categoriesLookup,
}: {
  noOfRows: number;
  onNameCellClick?: (report) => void;
  timesheetCategories: Category[];
  categoriesLookup: Dictionary<Category>;
}) => {
  return [
    {
      title: (
        <FormattedMessage
          id="timesheets.column.categories"
          values={{ count: noOfRows }}
        />
      ),
      key: 'categories',
      width: '310px',
      render: (_, record: TimesheetCategoryReport) => {
        const { category } = record;

        if (category) {
          const timesheetCategory = timesheetCategories.find(
            (c) => category.categoryId === c.id,
          );

          return (
            <TagBaseCell
              onClick={() => {
                if (onNameCellClick) {
                  onNameCellClick(record);
                }
              }}
              clickable={onNameCellClick != null}
            >
              <Tooltip
                title={
                  timesheetCategory?.deleted ? (
                    <FormattedMessage id="timesheets.labels.deleted_category" />
                  ) : null
                }
              >
                <Tag color={getRandomColor(category.categoryId)}>
                  <CategoryName
                    name={timesheetCategory?.name ?? category.categoryName}
                    deleted={timesheetCategory?.deleted}
                  />
                </Tag>
              </Tooltip>
            </TagBaseCell>
          );
        }

        return (
          <HoverableBaseCell
            clickable={onNameCellClick != null}
            onClick={() => {
              if (onNameCellClick) {
                onNameCellClick(record);
              }
            }}
          >
            <FormattedMessage id="timesheets.column.no_category" />
          </HoverableBaseCell>
        );
      },
      summaryRender: () => <FormattedMessage id="timesheets.column.total" />,
      defaultSortOrder: 'ascend',
      sorter: categoryNameSorter(categoriesLookup),
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.hours_tracked" />,
      key: 'hoursTracked',
      width: '200px',
      render: (_, record: TimesheetCategoryReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        return (
          <BaseCell>
            <FormatTime value={billableMinutes + nonBillableMinutes} />
          </BaseCell>
        );
      },
      summaryRender: (_, summaryRecord: TimesheetCategoryReport) => (
        <FormatTime
          value={
            summaryRecord.billableMinutes + summaryRecord.nonBillableMinutes
          }
        />
      ),
      sorter: totalMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.billable_hours" />,
      key: 'billableHours',
      width: '200px',
      render: (_, record: TimesheetCategoryReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          billableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={billableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetCategoryReport) => (
        <FormatTime value={summaryRecord.billableMinutes} />
      ),
      sorter: billableMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.non_billable_hours" />,
      key: 'nonBillableHours',
      render: (_, record: TimesheetCategoryReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          nonBillableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={nonBillableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetCategoryReport) => (
        <FormatTime value={summaryRecord.nonBillableMinutes} />
      ),
      sorter: nonBillableMinutesSorter,
      showSorterTooltip: false,
    },
  ];
};
